import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  auth,
  clearBearerToken,
  restoreBearerFromStorage,
  setBearerToken,
} from '../services/api';

const AuthContext = createContext(null);

// ── Session timeout constants (NIST SP 800-63B / OWASP ASVS) ──────────────
const IDLE_TIMEOUT_MS   = 30 * 60 * 1000;  // 30 min idle → logout
const WARNING_BEFORE_MS =  2 * 60 * 1000;  // show warning 2 min before logout
const ACTIVITY_EVENTS   = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];

// Decode JWT payload without verification (expiry check only)
function parseJwtExp(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // convert to ms
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showSessionWarning, setShowSessionWarning] = useState(false);
  const [warningCountdown, setWarningCountdown] = useState(120); // seconds

  const idleTimerRef      = useRef(null);
  const warningTimerRef   = useRef(null);
  const absExpiryTimerRef = useRef(null);
  const countdownRef      = useRef(null);
  const isAuthenticatedRef = useRef(false);

  // ── Internal logout (no server call — used for forced timeouts) ───────────
  const forceLogout = useCallback((reason) => {
    setShowSessionWarning(false);
    clearTimeout(idleTimerRef.current);
    clearTimeout(warningTimerRef.current);
    clearTimeout(absExpiryTimerRef.current);
    clearInterval(countdownRef.current);
    clearBearerToken();
    // Store logout reason for UX message on login page
    sessionStorage.setItem('logout_reason', reason);
    setUser(null);
    isAuthenticatedRef.current = false;
  }, []);

  // ── Reset idle timer on user activity ─────────────────────────────────────
  const resetIdleTimer = useCallback(() => {
    if (!isAuthenticatedRef.current) return;
    setShowSessionWarning(false);
    clearInterval(countdownRef.current);

    clearTimeout(idleTimerRef.current);
    clearTimeout(warningTimerRef.current);

    // Show warning at IDLE_TIMEOUT_MS − WARNING_BEFORE_MS
    warningTimerRef.current = setTimeout(() => {
      setWarningCountdown(WARNING_BEFORE_MS / 1000);
      setShowSessionWarning(true);
      // Countdown ticker
      countdownRef.current = setInterval(() => {
        setWarningCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);

    // Auto-logout at full idle timeout
    idleTimerRef.current = setTimeout(() => {
      forceLogout('idle');
    }, IDLE_TIMEOUT_MS);
  }, [forceLogout]);

  // ── Schedule absolute token expiry logout ─────────────────────────────────
  const scheduleAbsoluteExpiry = useCallback((jwtToken) => {
    clearTimeout(absExpiryTimerRef.current);
    const expMs = parseJwtExp(jwtToken);
    if (!expMs) return;
    const msUntilExp = expMs - Date.now();
    if (msUntilExp <= 0) {
      forceLogout('expired');
      return;
    }
    absExpiryTimerRef.current = setTimeout(() => {
      forceLogout('expired');
    }, msUntilExp);
  }, [forceLogout]);

  // ── Attach / detach activity listeners ────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    isAuthenticatedRef.current = true;
    resetIdleTimer();
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, resetIdleTimer, { passive: true }));
    return () => {
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, resetIdleTimer));
      clearTimeout(idleTimerRef.current);
      clearTimeout(warningTimerRef.current);
      clearInterval(countdownRef.current);
    };
  }, [user, resetIdleTimer]);

  // ── Token verification on mount — cookie (same-site) or Bearer (cross-origin dev) ──────────
  const verifyToken = useCallback(async () => {
    restoreBearerFromStorage();
    try {
      const response = await auth.getMe();
      setUser(response.data);
    } catch {
      clearBearerToken();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  // ── Refresh user data (e.g. after profile completion) ────────────────────
  const refreshUser = useCallback(async () => {
    try {
      const response = await auth.getMe();
      setUser(response.data);
    } catch {}
  }, []);

  // ── Login ──────────────────────────────────────────────────────────────────
  const login = useCallback(async (username, password) => {
    const response = await auth.login(username, password);
    const { access_token } = response.data;
    sessionStorage.removeItem('logout_reason');
    // Cross-site (e.g. localhost → dev-suite API): cookie often won't attach to XHR — use Bearer + storage for /auth/me
    setBearerToken(access_token);
    const meResp = await auth.getMe();
    const userData = meResp.data;
    setUser(userData);
    // Use token only to schedule absolute expiry timer, then discard
    scheduleAbsoluteExpiry(access_token);
    return userData;
  }, [scheduleAbsoluteExpiry]);

  // ── Voluntary logout ───────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    isAuthenticatedRef.current = false;
    clearTimeout(idleTimerRef.current);
    clearTimeout(warningTimerRef.current);
    clearTimeout(absExpiryTimerRef.current);
    clearInterval(countdownRef.current);
    setShowSessionWarning(false);
    try {
      await auth.logout(); // backend clears httpOnly cookie + increments token_version
    } catch (_) {}
    clearBearerToken();
    setUser(null);
  }, []);

  // ── "Stay logged in" — reset idle timer ───────────────────────────────────
  const extendSession = useCallback(() => {
    resetIdleTimer();
  }, [resetIdleTimer]);

  const value = {
    user,
    isLoading,
    login,
    logout,
    refreshUser,
    extendSession,
    showSessionWarning,
    warningCountdown,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
